import { useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Container,
  Image,
  Grid,
  Input,
  InputGroup,
  InputRightElement,
  VStack,
} from '@chakra-ui/react';
import { FiSearch } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import bgInfographics from '../../assets/webp/bg-infographics.webp';
import infographicsGirl from '../../assets/webp/infographics.webp';
import { useLandingDataContext } from '../../controller/public/landing_data';
import Pagination from '../Pagination';

const ITEMS_PER_PAGE = 5;

const Infografis = () => {
  const history = useHistory();
  const { landingData } = useLandingDataContext().state;
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const provinceDataWithPercentage = useMemo(() => {
    const provinceData = landingData?.peserta_provinsi || [];
    const totalPeserta = landingData?.count_peserta || 0;

    return provinceData.map((province) => ({
      ...province,
      presentase:
        totalPeserta > 0
          ? Number(((province.total_peserta / totalPeserta) * 100).toFixed(2))
          : 0,
    }));
  }, [landingData]);

  const filteredProvinces = useMemo(() => {
    if (!searchQuery.trim()) return provinceDataWithPercentage;
    return provinceDataWithPercentage.filter((province) =>
      province.nama_provinsi.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [provinceDataWithPercentage, searchQuery]);

  const totalPages = Math.ceil(filteredProvinces.length / ITEMS_PER_PAGE);
  const paginatedProvinces = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredProvinces.slice(startIndex, endIndex);
  }, [filteredProvinces, currentPage]);

  const handlePageChange = (e: { selected: number }) => {
    setCurrentPage(e.selected + 1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleNavigateToLogin = () => {
    history.push('/login');
  };

  return (
    <Box bg="green.50">
      <Container maxW="8xl" my={{ base: 8, md: 20 }}>
        <Flex direction={{ base: 'column', md: 'row' }} className="gap-10">
          {/* Card */}
          <Box
            bgImage={`url(${bgInfographics})`}
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            p={14}
            w="437px"
            h="665px"
            className="rounded-3xl"
            position="relative"
            display={{ base: 'none', md: 'block' }}
          >
            <Text fontSize="3xl" fontWeight="semibold" color="white" mb={4}>
              Menjadi pintar dengan PINTAR sekarang !
            </Text>
            <Text color="white" fontSize="md" mb={14}>
              Ayo bergabung dengan PINTAR dan tingkatkan kompetensi diri Anda
              bersama ribuan orang lainnya!
            </Text>

            <button
              className="border border-white rounded-md text-white h-12 px-6 hover:bg-white hover:text-black"
              onClick={handleNavigateToLogin}
            >
              Yuk Mulai Belajar
            </button>

            <Image
              src={infographicsGirl}
              position="absolute"
              bottom={0}
              right={0}
              w="240px"
              className="rounded-br-3xl"
            />
          </Box>

          <Flex
            direction="column"
            flex={{ base: '1', md: '1' }}
            w={{ base: '100%', md: 'auto' }}
            overflow="hidden"
          >
            <Text fontSize="3xl" fontWeight="semibold" color="black">
              Infografis PINTAR
            </Text>
            <Text fontSize="md">
              berikut adalah data PINTAR (Pusat Informasi pelatihan &
              pembelajaran ) Kementerian Agama RI
            </Text>

            {/* Total Data Section */}
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
              gap={4}
              mt={6}
            >
              <Box
                bg="white"
                px={3}
                py={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <Text fontSize="sm" color="gray.600">
                  Total Peserta
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="green.500">
                  {landingData?.count_peserta}
                </Text>
              </Box>
              <Box
                bg="white"
                px={3}
                py={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <Text fontSize="sm" color="gray.600">
                  Jumlah Pelatihan
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="green.500">
                  {landingData?.count_pelatihan}
                </Text>
              </Box>
              <Box
                bg="white"
                px={3}
                py={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="sm"
              >
                <Text fontSize="sm" color="gray.600">
                  Modul Belajar
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="green.500">
                  {landingData?.count_materi}
                </Text>
              </Box>
            </Grid>

            {/* Sebaran per Provinsi Section */}
            <Box
              bg="white"
              p={6}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.200"
              mt={2}
            >
              {/* Header with title and search */}
              <Flex
                justify="space-between"
                align="center"
                mb={4}
                flexDirection={{ base: 'column', md: 'row' }}
                gap={4}
              >
                <Text
                  fontSize="xl"
                  fontWeight="semibold"
                  mb={{ base: 4, md: 0 }}
                >
                  Sebaran per Provinsi
                </Text>
                <InputGroup maxW={{ base: '100%', md: '300px' }}>
                  <Input
                    placeholder="Cari disini...."
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="full"
                    _placeholder={{ color: 'gray.400' }}
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <InputRightElement>
                    <FiSearch color="gray" />
                  </InputRightElement>
                </InputGroup>
              </Flex>

              {/* Province List */}
              <VStack spacing={0} align="stretch" className="gap-2">
                {paginatedProvinces.map((province, index) => {
                  // Calculate the actual index based on current page
                  const displayIndex =
                    (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
                  return (
                    <Flex
                      key={index}
                      align="center"
                      p={2}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="lg"
                      _hover={{ bg: 'green.50', borderColor: 'green.300' }}
                      transition="background 0.2s"
                      flexWrap="wrap"
                      gap={{ base: 2, md: 0 }}
                    >
                      {/* Number */}
                      <Text
                        fontSize="lg"
                        fontWeight="medium"
                        color="gray.700"
                        minW="50px"
                      >
                        {String(displayIndex).padStart(2, '0')}
                      </Text>

                      {/* Province Name */}
                      <Text
                        fontSize="md"
                        fontWeight="medium"
                        color="gray.700"
                        flex={1}
                      >
                        {province.nama_provinsi}
                      </Text>

                      {/* Jumlah Peserta */}
                      <Box
                        textAlign="center"
                        minW={{ base: 'auto', md: '120px' }}
                        flex={{ base: '1', md: 'none' }}
                      >
                        <Text fontSize="xs" color="gray.500">
                          Jumlah Peserta
                        </Text>
                        <Text
                          fontSize="md"
                          fontWeight="semibold"
                          color="gray.700"
                        >
                          {province.total_peserta}
                        </Text>
                      </Box>

                      {/* Presentase */}
                      <Box
                        textAlign="center"
                        minW={{ base: 'auto', md: '100px' }}
                        flex={{ base: '1', md: 'none' }}
                      >
                        <Text fontSize="xs" color="gray.500">
                          Presentase
                        </Text>
                        <Text
                          fontSize="md"
                          fontWeight="semibold"
                          color="gray.700"
                        >
                          {province.presentase}%
                        </Text>
                      </Box>
                    </Flex>
                  );
                })}
              </VStack>
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
              <Flex justifyContent="end" mt={4}>
                <Pagination
                  handleChange={handlePageChange}
                  totalPage={totalPages}
                  page={currentPage}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};

export default Infografis;
